import React, { useCallback, useState } from 'react';
import { useForm as useHookForm, FormProvider } from 'react-hook-form';
import {
  BenchmarkScreen,
  useBenchmarkDetails,
  useTWNavigation,
  useTWScreenParams,
  useTimeWindows,
} from 'hooks';
import { Footer, TableTabbed } from 'components';
import { useTable, useTimeWindow } from './hooks';
import { BenchmarkForm } from '../../components/BenchmarkMetadata/components/BenchmarkForm/BenchmarkForm';
import { TimeWindowForm } from './PullForwardTimeWindow';
import { useYupValidationResolver } from '../../helpers/form';
import { DevTool } from '@hookform/devtools';
import { useEffect } from 'react';
import { showSuccess } from 'helpers/showSuccess';
import { useSubmit } from 'hooks/useSubmit';
import { getBody } from './PullForwardHelpers';
import {
  PullForward as PullForwardType,
  PullForwardTimeWindow,
} from 'types/PullForward';
import { BenchmarkMetadata } from 'components/BenchmarkMetadata';
import { SmallDisplay } from './components/SmallDisplay';
import { DefaultDisplay } from './components/DefaultDisplay';
import { TimeWindowsEditTitle, TimeWindows } from 'components/TimeWindow';
import { getToDates } from 'helpers/timeWindow/getToDates';
import { BenchmarkTypes } from 'types/Benchmark';
import { BenchmarkFormTitle } from 'components/BenchmarkMetadata/components/BenchmarkForm/BenchmarkFormStyles';
import {
  pullForwardTimeWindowShape,
  pullForwardValidationScheme,
} from './PullForwardValidationScheme';
import { useProblemsHandler } from 'modules/BenchmarkForm/hooks/useProblemsHandler';
import { LoadingWrapper } from 'components/LoadingWrapper/LoadingWrapper';

type Props = {};

export const PullForward: React.FC<Props> = () => {
  const [isFormLoadComplete, setIsFormLoadComplete] = useState(false);
  const resolver = useYupValidationResolver(pullForwardValidationScheme());

  const {
    benchmark,
    refetch,
    isLoading: isLoadingBenchmark,
  } = useBenchmarkDetails<PullForwardType>();
  const { isTimeWindowsScreen, addTimeWindow, benchmarkScreen } =
    useTWScreenParams(benchmark?.id);
  const handleTWNavigation = useTWNavigation(benchmarkScreen);

  const methods = useHookForm<any>({
    resolver,
    defaultValues: {
      baseCurrencyCode: 'USD',
    },
  });

  const {
    handleSubmit,
    formState: { isDirty },
    control,
    reset,
    setValue,
    setError,
  } = methods;

  const { timeWindows, timeWindowsFilled } =
    useTimeWindows<PullForwardTimeWindow>({
      setValue,
      control,
      timeWindowSchema: pullForwardTimeWindowShape,
    });
  const tableProps = useTable(timeWindows);

  const {
    onSubmit,
    isSuccess,
    isLoading: isSubmitting,
    errors: problemDetails,
  } = useSubmit({
    originalValues: benchmark,
    getBody,
    originalTimeWindows: timeWindows,
  });

  useProblemsHandler(setError, problemDetails);

  useEffect(() => {
    if (isSuccess) {
      if (benchmark?.id) {
        showSuccess('Benchmark was successfully saved');
        refetch();
        handleTWNavigation();
        window.scrollTo(0, 0);
      }
    }
  }, [isSuccess, benchmark?.id]);

  const resetData = useCallback(() => {
    reset();
    if (benchmark) {
      let values: any = {
        ...benchmark,
        timeWindows: getToDates(
          benchmark?.timeWindows?.map((item: any) => {
            return {
              fromDate: new Date(item.fromDate),
              returnComponent:
                item.returnComponentBenchmarkId ||
                item.returnComponentResourceId,
              numberPeriods: item.pullForwardLength,
              name: item.returnComponentName
            };
          })
        ),
      };

      reset(values);
    }
  }, [benchmark, reset]);

  useEffect(() => {
    resetData();
  }, [resetData]);

  const tableTabbedProps = {
    ...tableProps,
    useTimeWindow,
    handleSubmit,
    onSubmit,
    isLoading: isSubmitting || isLoadingBenchmark,
    benchmarkName: benchmark?.name,
    benchmarkId: benchmark?.id,
  };

  const renderContent = () => {
    if (isLoadingBenchmark) return null;

    if (isTimeWindowsScreen || addTimeWindow) {
      return (
        <TimeWindows
          benchmarkType={BenchmarkTypes.PullForward}
          timeWindowsFilled={timeWindowsFilled}
          benchmarkId={benchmark?.id}
          TimeWindowComponent={TimeWindowForm}
          defaultValues={{
            fromDate: new Date(),
            numberPeriods: null,
            returnComponent: null,
          }}
        />
      );
    }

    return <TableTabbed {...tableTabbedProps} />;
  };

  return (
    <LoadingWrapper isLoading={isLoadingBenchmark && isFormLoadComplete}>
      <TimeWindowsEditTitle isTimeWindowsScreen={isTimeWindowsScreen} />
      {benchmark?.id && !isSubmitting ? (
        <BenchmarkMetadata
          benchmark={benchmark}
          onConfirm={refetch}
          isTimeWindowsScreen={isTimeWindowsScreen}
        >
          <SmallDisplay benchmark={benchmark} />
          <DefaultDisplay benchmark={benchmark} />
        </BenchmarkMetadata>
      ) : null}
      <FormProvider {...methods}>
        {benchmark === undefined ? (
          <div style={{ padding: 20 }}>
            <BenchmarkFormTitle>
              Add New Pull Forward Benchmark
            </BenchmarkFormTitle>
            <BenchmarkForm
              benchmarkType={BenchmarkTypes.PullForward}
              benchmark={benchmark}
              onLoadComplete={() => setIsFormLoadComplete(true)}
            />
          </div>
        ) : null}
        {renderContent()}
        <Footer
          showReturnHome={benchmarkScreen === BenchmarkScreen.editBenchmark}
          onSave={handleSubmit((value: any) => {
            onSubmit({
              ...value,
              onSaveSuccess: isSuccess,
            });
          })}
          onDiscardChanges={() => {
            resetData();
            handleTWNavigation();
            window.scrollTo(0, 0);
          }}
          isSaveDisabled={!isDirty}
          isLoading={tableTabbedProps.isLoading}
          showEditButtons={Boolean(isTimeWindowsScreen || !benchmark?.id)}
          discardText='Cancel'
          isDirty={isDirty}
        />
        <DevTool control={control} placement='top-left' />
      </FormProvider>
    </LoadingWrapper>
  );
};
